class KlaviyoBis extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      // EMAIL pane
      formEmail: '[js-klaviyo-bis-form]',
      emailInput: '[js-klaviyo-bis-email]',
      submitEmailBtn: '[js-klaviyo-bis-submit]',
      loaderEmail: '[js-klaviyo-bis-loader]',
      messageEmail: '[js-klaviyo-bis-message]',
      submitEmailText: '.klaviyo-bis__submit-text',
      successEmail: '[js-klaviyo-bis-success]',
      newsletterCheckbox: 'input[name="subscribe"]',

      // VARIANT
      variantSelect: '[js-klaviyo-bis-select]',

      // SMS pane
      formSms: '[js-klaviyo-sms-form]',
      smsPhone: '[js-klaviyo-sms-phone]',
      smsCountry: '[js-klaviyo-sms-country]',
      smsSubmitBtn: '[js-klaviyo-sms-submit]',
      successSms: '[js-klaviyo-sms-success]',

      // Pane toggles
      paneEmail: '[data-pane="email"]',
      paneSms: '[data-pane="sms"]',
      toSmsLink: '[js-klaviyo-bis-to-sms]',
      toEmailLink: '[js-klaviyo-bis-to-email]',

      // Close / modal wrapper
      closeBtn: '[js-klaviyo-bis-close]',
      modalOverlay: '[data-klaviyo-bis-modal]'
    };

    // IMPORTANT: should match this store's Klaviyo public company_id
    this.companyId = 'KCMxxt'; // update if needed

    // Newsletter list ID for opt-in.
    // Replace "SRwALh" with your actual Klaviyo list ID (or remove _subscribeToNewsletter).
    this.listIdForNewsletter = 'SRwALh';
  }

  connectedCallback() {
    // Cache DOM we care about
    this.modalWrapper = document.querySelector(this._selectors.modalOverlay);

    // Email elements
    this.formEmail = this.querySelector(this._selectors.formEmail);
    this.emailInput = this.querySelector(this._selectors.emailInput);
    this.submitEmailBtn = this.querySelector(this._selectors.submitEmailBtn);
    this.loaderEmail = this.querySelector(this._selectors.loaderEmail);
    this.messageEmail = this.querySelector(this._selectors.messageEmail);
    this.submitEmailText = this.querySelector(this._selectors.submitEmailText);
    this.successEmail = this.querySelector(this._selectors.successEmail);
    this.newsletterCheckbox = this.querySelector(this._selectors.newsletterCheckbox);

    // Variant select (even if hidden visually)
    this.variantSelect = this.querySelector(this._selectors.variantSelect);

    // SMS elements
    this.formSms = this.querySelector(this._selectors.formSms);
    this.smsPhone = this.querySelector(this._selectors.smsPhone);
    this.smsCountry = this.querySelector(this._selectors.smsCountry);
    this.smsSubmitBtn = this.querySelector(this._selectors.smsSubmitBtn);
    this.successSms = this.querySelector(this._selectors.successSms);

    // Panes + toggle links
    this.paneEmail = this.querySelector(this._selectors.paneEmail);
    this.paneSms = this.querySelector(this._selectors.paneSms);
    this.toSmsLinks = this.querySelectorAll(this._selectors.toSmsLink);
    this.toEmailLinks = this.querySelectorAll(this._selectors.toEmailLink);

    // Close button is OUTSIDE <klaviyo-bis>, so get it from wrapper:
    this.closeBtn = this.modalWrapper?.querySelector(this._selectors.closeBtn);

    // Initialize
    this._hydrateVariantMap();
    this._updateVariantId();
    this._bindEvents();
  }

  _bindEvents() {
    // Email form submit
    this.formEmail?.addEventListener('submit', this._handleSubmitEmail.bind(this));

    // Variant dropdown (if you ever unhide it)
    this.variantSelect?.addEventListener('change', this._handleVariantChange.bind(this));

    // SMS form submit
    this.formSms?.addEventListener('submit', this._handleSubmitSms.bind(this));

    // Toggle to SMS
    this.toSmsLinks?.forEach(link => {
      link.addEventListener('click', () => {
        this._showSmsPane();
      });
    });

    // Toggle back to Email
    this.toEmailLinks?.forEach(link => {
      link.addEventListener('click', () => {
        this._showEmailPane();
      });
    });

    // Close button
    this.closeBtn?.addEventListener('click', this._handleClose.bind(this));

    // Click outside modal (overlay background)
    this.modalWrapper?.addEventListener('click', (ev) => {
      if (ev.target === this.modalWrapper) {
        this._handleClose();
      }
    });
  }

  /* ---------- Pane switching ---------- */

  _showSmsPane() {
    if (this.paneEmail) this.paneEmail.classList.add('hidden');
    if (this.paneSms) this.paneSms.classList.remove('hidden');
  }

  _showEmailPane() {
    if (this.paneSms) this.paneSms.classList.add('hidden');
    if (this.paneEmail) this.paneEmail.classList.remove('hidden');
  }

  /* ---------- Modal close ---------- */

  _handleClose() {
    if (this.modalWrapper) {
      this.modalWrapper.classList.remove('is-active');
      this.modalWrapper.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('no-scroll');
    }
    // Reset pane to email for next open
    this._showEmailPane();
  }

  /* ---------- Variant handling ---------- */

  _hydrateVariantMap() {
    // Build a label->id map from <select js-klaviyo-bis-select>
    this.variantMap = {};
    if (!this.variantSelect) return;

    [...this.variantSelect.options].forEach(opt => {
      const vId = opt.getAttribute('data-variant-id');
      if (vId) {
        this.variantMap[opt.value] = vId;
      }
    });
  }

  _handleVariantChange() {
    const selectedLabel = this.variantSelect.value;
    const rawVariantId = this.variantMap[selectedLabel];
    this._setVariantDataset(rawVariantId);
  }

  _setVariantDataset(rawVariantId) {
    if (rawVariantId) {
      this.dataset.variantId = rawVariantId;
      this._updateVariantId();
    }
  }

  _updateVariantId() {
    const rawVariantId = this.dataset.variantId || '';
    // Klaviyo wants Shopify variant IDs formatted like this:
    this.variantId = rawVariantId ? `$shopify:::$default:::${rawVariantId}` : '';
  }

  // called externally from the trigger before opening modal
  updateVariantFromOutside(variantId, variantLabel) {
    this._setVariantDataset(variantId);

    // Sync hidden dropdown so variantMap knows about it
    if (this.variantSelect) {
      const existingOpt = [...this.variantSelect.options].find(
        o => o.getAttribute('data-variant-id') === String(variantId)
      );

      if (existingOpt) {
        this.variantSelect.value = existingOpt.value;
      } else {
        // mutate first option to be this variant
        const firstOpt = this.variantSelect.options[0];
        if (firstOpt) {
          firstOpt.textContent = variantLabel;
          firstOpt.value = variantLabel;
          firstOpt.setAttribute('data-variant-id', variantId);

          // re-hydrate map so dropdown→id works if they change it later
          this._hydrateVariantMap();
          this.variantSelect.value = variantLabel;
        }
      }
    }
  }

  /* ---------- EMAIL submit flow ---------- */

  _handleSubmitEmail(event) {
    event.preventDefault();

    const email = this.emailInput?.value?.trim();
    const subscribeToNewsletter = this.newsletterCheckbox?.checked || false;

    if (!email || !this._isValidEmail(email)) {
      this._showEmailMessage('Please enter a valid email address.');
      return;
    }

    if (!this.variantId) {
      this._showEmailMessage('No variant selected.');
      return;
    }

    this._setLoadingEmail(true);
    this._submitToKlaviyoEmail(email, subscribeToNewsletter);
  }

  _isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  _submitToKlaviyoEmail(email, subscribeToNewsletter) {
    const requestBody = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email
              }
            }
          },
          channels: ["EMAIL"]
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: this.variantId
            }
          }
        }
      }
    };

    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
        'revision': '2024-02-15',
        'x-klaviyo-onsite': '1'
      },
      body: JSON.stringify(requestBody),
      mode: 'cors',
      credentials: 'omit'
    })
    .then(response => {
      if (response.ok) {
        // Hide submit button, show success
        if (this.submitEmailBtn) this.submitEmailBtn.style.display = 'none';
        if (this.successEmail) this.successEmail.classList.remove('hidden');

        if (subscribeToNewsletter) {
          this._subscribeToNewsletterEmail(email);
        }
      } else {
        throw new Error('Network response was not ok');
      }
    })
    .catch(error => {
      console.error('Klaviyo BIS error:', error);
      this._showEmailMessage('Something went wrong. Please try again.');
    })
    .finally(() => {
      this._setLoadingEmail(false);
    });
  }

  _setLoadingEmail(isLoading) {
    if (isLoading) {
      this.submitEmailBtn?.setAttribute('disabled', '');
      this.loaderEmail?.classList.remove('hidden');
      this.submitEmailText?.classList.add('hidden');
    } else {
      this.submitEmailBtn?.removeAttribute('disabled');
      this.loaderEmail?.classList.add('hidden');
      this.submitEmailText?.classList.remove('hidden');
    }
  }

  _showEmailMessage(text) {
    if (this.messageEmail) {
      this.messageEmail.textContent = text;
      this.messageEmail.classList.remove('hidden');
    }
  }

  _subscribeToNewsletterEmail(email) {
    if (!this.listIdForNewsletter) return;

    const requestBody = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                properties: {
                  "$source": "Back in Stock"
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: this.listIdForNewsletter
            }
          }
        }
      }
    };

    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this.companyId}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
        'revision': '2024-02-15',
        'x-klaviyo-onsite': '1'
      },
      body: JSON.stringify(requestBody),
      mode: 'cors',
      credentials: 'omit'
    })
    .then(response => {
      if (!response.ok) {
        console.error('Newsletter subscription failed');
      }
    })
    .catch(error => {
      console.error('Newsletter subscription error:', error);
    });
  }

  /* ---------- SMS submit flow ---------- */

  _handleSubmitSms(event) {
    event.preventDefault();

    const phone = this.smsPhone?.value?.trim();
    const countryCode = this.smsCountry?.value || '';

    if (!phone) {
      console.warn('No phone provided');
      this._showSmsSuccess('Please enter a valid phone number.');
      return;
    }

    // TODO: Wire this up to your Klaviyo SMS subscription endpoint.
    // For now we just pretend success and show a message.
    console.log('TODO submit SMS to Klaviyo:', {
      phone: phone,
      countryCode: countryCode,
      variantId: this.variantId
    });

    if (this.successSms) {
      this.successSms.classList.remove('hidden');
    }
  }

  _showSmsSuccess(msg) {
    if (this.successSms) {
      this.successSms.textContent = msg;
      this.successSms.classList.remove('hidden');
    }
  }
}

/* Trigger element that shows the modal */
class KlaviyoBisTrigger extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
  }

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this._handleClick.bind(this));
    }
  }

  _handleClick() {
    const modalWrapper = document.querySelector('[data-klaviyo-bis-modal]');
    const klaviyoBisEl = modalWrapper?.querySelector('klaviyo-bis');
    const variantSelectEl = modalWrapper?.querySelector('[js-klaviyo-bis-select]');

    const variantId = this.dataset.variantId;
    const variantLabel = this.dataset.productVariantOption;

    // Update variant info on the <klaviyo-bis> element
    if (klaviyoBisEl && typeof klaviyoBisEl.updateVariantFromOutside === 'function') {
      klaviyoBisEl.updateVariantFromOutside(variantId, variantLabel);
    }

    // Update dropdown first option so it's in sync
    if (variantSelectEl && variantSelectEl.options[0]) {
      variantSelectEl.options[0].textContent = variantLabel;
      variantSelectEl.options[0].value = variantLabel;
      variantSelectEl.options[0].setAttribute('data-variant-id', variantId);
      variantSelectEl.value = variantLabel;
    }

    // Open modal
    if (modalWrapper) {
      modalWrapper.classList.add('is-active');
      modalWrapper.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('no-scroll');
    }
  }
}

/* Register custom elements */
if (!customElements.get('klaviyo-bis')) {
  customElements.define('klaviyo-bis', KlaviyoBis);
}
if (!customElements.get('klaviyo-bis-trigger')) {
  customElements.define('klaviyo-bis-trigger', KlaviyoBisTrigger);
}

/* OPTIONAL:
   if your Dawn build does NOT fully re-render buy-buttons on variant change,
   keep this listener to update the trigger dynamically.
*/
document.addEventListener('variant:change', (event) => {
  const newVariant = event.detail && event.detail.variant;
  if (!newVariant) return;

  const triggerEl = document.querySelector('klaviyo-bis-trigger');
  if (!triggerEl) return;

  triggerEl.dataset.variantId = newVariant.id;
  triggerEl.dataset.productVariantOption = newVariant.title;

  // Hide trigger if new variant is available, show if sold out
  if (newVariant.available) {
    triggerEl.style.display = 'none';
  } else {
    triggerEl.style.display = '';
    const btn = triggerEl.querySelector('button[data-action="open-klaviyo-bis"]') ||
                triggerEl.querySelector('button');
    if (btn) {
      btn.disabled = false;
      const span = btn.querySelector('span');
      if (span) {
        span.textContent = 'Notify me when available';
      }
    }
  }
});